import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "./Dashboard";
import { Modal, Field, Th, Td } from "./Customers";
import { Plus, AlertTriangle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

export default function Items() {
  const { tenant } = useAuth();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);

  const load = () => api.get("/items").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);

  return (
    <div className="p-4 sm:p-6 lg:p-12 max-w-[1400px]" data-testid="items-page">
      <PageHeader
        title="Items & Stock"
        subtitle="Catalogue with live stock-on-hand. Stock decrements automatically on invoiced sales orders."
        action={
          <button onClick={() => setOpen(true)} data-testid="add-item-btn"
            className="inline-flex items-center gap-2 bg-[#0A0A0A] hover:bg-[#FF4F00] text-white px-4 py-2.5 text-sm font-medium rounded-sm transition-colors">
            <Plus size={14} /> New item
          </button>
        }
      />

      <div className="mt-8 border border-[#0A0A0A] bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#0A0A0A] text-white text-[11px] uppercase tracking-[0.15em]">
            <tr>
              <Th>SKU</Th>
              <Th>Name</Th>
              <Th>Category</Th>
              <Th align="right">Price</Th>
              <Th align="right">Cost</Th>
              <Th align="right">Stock</Th>
              <Th align="right">Reorder lvl</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((it, i) => {
              const low = it.stock_on_hand <= it.reorder_level;
              return (
                <tr key={it.id} data-testid={`item-row-${i}`} className="border-b border-[#DEE2E6] hover:bg-[#F8F9FA]">
                  <Td className="font-mono text-xs">{it.sku}</Td>
                  <Td><div className="font-medium">{it.name}</div></Td>
                  <Td className="text-[#495057]">{it.category || "—"}</Td>
                  <Td align="right" className="tabular-nums">{tenant?.currency || ""} {it.price.toLocaleString()}</Td>
                  <Td align="right" className="tabular-nums text-[#495057]">{it.cost.toLocaleString()}</Td>
                  <Td align="right" className={`tabular-nums font-semibold ${low ? "text-[#D90429]" : "text-[#0A0A0A]"}`}>
                    {low && <AlertTriangle size={12} className="inline mr-1 -mt-0.5" />}
                    {it.stock_on_hand}
                  </Td>
                  <Td align="right" className="tabular-nums text-[#495057]">{it.reorder_level}</Td>
                </tr>
              );
            })}
            {rows.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-[#495057]">No items yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {open && <ItemForm onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
    </div>
  );
}

function ItemForm({ onClose, onSaved }) {
  const [f, setF] = useState({ sku: "", name: "", description: "", uom: "EA", price: 0, cost: 0, stock_on_hand: 0, reorder_level: 0, category: "" });
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true); setErr("");
    try {
      await api.post("/items", {
        ...f,
        price: parseFloat(f.price || 0),
        cost: parseFloat(f.cost || 0),
        stock_on_hand: parseFloat(f.stock_on_hand || 0),
        reorder_level: parseFloat(f.reorder_level || 0),
      });
      onSaved();
    } catch (e) { setErr(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <Modal title="New item" onClose={onClose} testid="item-modal" wide>
      <form onSubmit={save} className="grid grid-cols-2 gap-4" data-testid="item-form">
        <Field label="SKU" required value={f.sku} onChange={(v) => setF({ ...f, sku: v })} testid="item-sku" />
        <Field label="UoM" value={f.uom} onChange={(v) => setF({ ...f, uom: v })} testid="item-uom" />
        <div className="col-span-2"><Field label="Name" required value={f.name} onChange={(v) => setF({ ...f, name: v })} testid="item-name" /></div>
        <div className="col-span-2"><Field label="Category" value={f.category} onChange={(v) => setF({ ...f, category: v })} testid="item-category" /></div>
        <Field label="Price" type="number" value={f.price} onChange={(v) => setF({ ...f, price: v })} testid="item-price" />
        <Field label="Cost" type="number" value={f.cost} onChange={(v) => setF({ ...f, cost: v })} testid="item-cost" />
        <Field label="Opening stock" type="number" value={f.stock_on_hand} onChange={(v) => setF({ ...f, stock_on_hand: v })} testid="item-stock" />
        <Field label="Reorder level" type="number" value={f.reorder_level} onChange={(v) => setF({ ...f, reorder_level: v })} testid="item-reorder" />
        {err && <div className="col-span-2 text-sm text-[#D90429]">{err}</div>}
        <div className="col-span-2 flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-[#DEE2E6]">Cancel</button>
          <button type="submit" disabled={saving} data-testid="item-save"
            className="px-4 py-2 text-sm bg-[#0A0A0A] hover:bg-[#FF4F00] text-white">Save</button>
        </div>
      </form>
    </Modal>
  );
}
